import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FramesService } from "../../routes/services/frames.service";
import { GlobalFunction } from "../../routes/services/globalFuntion";
import { StorageService } from "../../routes/services/storage.service";

/** Estructura de cada respuesta recibida por socket para frames de velocidad */
export interface SpeedConfigResponse {
  feeder: { name: string; mac: string };
  type: "SPEED_RESPONSE" | "ORBITAL_SPEED" | "SPEED_CONFIG";
  time: string;
}

/** Datos internos de configuración de velocidad masiva */
interface SpeedData {
  status_high_speed: number;
  status_low_speed: number;
  status_speed: number;
  view_mode_speed: "normal" | "orbital";
}

/** Estado de respuesta por alimentador durante la acción en curso */
interface FeederLoadStatus {
  name: string;
  mac: string;
  status: "loading" | "done" | "error";
  type?: string;
  time?: string;
  failReason?: string;
  data?: any;
}

@Component({
  selector: "app-speed-config",
  templateUrl: "./speed-config.component.html",
  styleUrls: ["./speed-config.component.scss"]
})
export class SpeedConfigComponent implements OnInit, OnDestroy {
  /** Lista de alimentadores seleccionados sobre los que se aplicarán los comandos */
  @Input() selectedFeeders: any[] = [];

  private destroy$ = new Subject<void>();

  dataMasive: SpeedData = {
    status_high_speed: 75,
    status_low_speed: 50,
    status_speed: 75,
    view_mode_speed: "orbital"
  };

  optionSpeed = [37.5, 50, 62.5, 75, 87.5, 100];

  responses: SpeedConfigResponse[] = [];
  feederStatuses: FeederLoadStatus[] = [];
  filterName = "";
  filterStatus = "";

  /** Acción actualmente en espera de respuesta; null si no hay ninguna en curso */
  loadingAction: "send" | "turnOff" | "turnOn" | "requestStatus" | null = null;

  private pendingMacs = new Set<string>();
  private expectedResponseTypes = new Set<string>();

  readonly frameTypes = ["SPEED_RESPONSE", "ORBITAL_SPEED", "SPEED_CONFIG", "ALERT_COMM"];

  readonly typeLabels: Record<string, string> = {
    SPEED_RESPONSE: "Velocidad",
    ORBITAL_SPEED: "Velocidad orbital",
    SPEED_CONFIG: "Configuración"
  };

  constructor(
    public storageService: StorageService,
    public framesService: FramesService,
    public globalFunction: GlobalFunction
  ) {}

  /** @inheritdoc */
  ngOnInit(): void {
    this.initData();
    this.storageService
      .watchStorage("socket")
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg: any) => {
        if (msg && msg.data && msg.data.type) {
          const { type, mac, name } = msg.data;

          if (type === "ALERT_COMM") {
            const requestType = msg.data.request && msg.data.request.type;
            if (requestType && this.frameTypes.includes(requestType)) {
              this.markFeederAsError(mac, msg.data.msg || "Error de comunicación");
            }
            return;
          }

          if (this.frameTypes.includes(type)) {
            this.responses.push({
              feeder: { name: name || mac || "—", mac },
              type,
              time: new Date().toLocaleTimeString()
            });
            this.checkLoadingComplete(mac, type, msg.data);
          }
        }
      });
  }

  /** @inheritdoc */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializa dataMasive con valores del primer alimentador que tenga velocidad activa.
   * Si ninguno tiene velocidad configurada, usa valores por defecto.
   */
  initData(): void {
    const feeder = this.selectedFeeders.find(
      (item) =>
        this.globalFunction.validateVersion(item, 0.5) &&
        (item.status_speed_mode === 1 || item.status_speed_mode === 2)
    );
    if (feeder) {
      const { status_high_speed, status_low_speed, status_speed, status_speed_mode } = feeder;
      const modeMap: Record<number, "normal" | "orbital"> = { 1: "normal", 2: "orbital" };
      this.dataMasive = {
        status_high_speed,
        status_low_speed,
        status_speed,
        view_mode_speed: modeMap[status_speed_mode] || "orbital"
      };
    }
  }

  /**
   * Retorna true si el modo de velocidad actual es orbital.
   * @returns boolean
   */
  isOrbital(): boolean {
    return this.dataMasive.view_mode_speed === "orbital";
  }

  /**
   * Retorna true si el botón Enviar debe estar deshabilitado por valores inválidos.
   * @returns boolean
   */
  isSendDisabled(): boolean {
    const { status_high_speed, status_low_speed, status_speed, view_mode_speed } = this.dataMasive;
    if (view_mode_speed === "orbital") {
      const high = Number(status_high_speed);
      const low = Number(status_low_speed);
      return !(high > low && high >= 37.5 && low >= 37.5 && high <= 100 && low <= 100);
    }
    return Number(status_speed) < 37.5;
  }

  /**
   * Envía el frame de configuración de velocidad a los alimentadores seleccionados.
   * Separa los de versión NAS0.5 de los normales y envía peticiones adecuadas.
   */
  send(): void {
    const { view_mode_speed } = this.dataMasive;
    const expectedType = view_mode_speed === "orbital" ? "ORBITAL_SPEED" : "SPEED_RESPONSE";
    this.startLoading("send", expectedType);

    const v5 = this.selectedFeeders.filter((item) => item.status_version === "NAS0.5");
    const normal = this.selectedFeeders.filter((item) => item.status_version !== "NAS0.5");

    if (v5.length > 0) {
      this.sendConfigThenEnable(v5, true);
    }

    if (normal.length > 0) {
      this.sendConfigThenEnable(normal, false);
    }
  }

  /**
   * Envía configuración y luego enciende el modo seleccionado.
   * @param feeders - subconjunto de alimentadores destino
   * @param forceV5 - true para usar flujo/configuración NAS0.5
   */
  private sendConfigThenEnable(feeders: any[], forceV5: boolean): void {
    this.sendConfigForGroup(feeders, forceV5, () => this.enableModeForGroup(feeders));
  }

  /**
   * Envía la configuración de velocidad al subconjunto indicado.
   * @param feeders - subconjunto de alimentadores destino
   * @param forceV5 - true para usar set_orbital_speed en NAS0.5
   * @param onSuccess - callback ejecutado cuando set_* responde sin error HTTP ni errores por MAC
   */
  private sendConfigForGroup(feeders: any[], forceV5: boolean, onSuccess?: () => void): void {
    const { view_mode_speed, status_high_speed, status_low_speed, status_speed } = this.dataMasive;
    const query = `?id_in=${feeders.map((f) => f.id)}`;

    const petition: "set_orbital_speed" | "set_speed" =
      forceV5 || view_mode_speed === "orbital" ? "set_orbital_speed" : "set_speed";

    const obj = forceV5
      ? { high: status_speed, low: status_speed, orbital_enabled: true }
      : view_mode_speed === "orbital"
      ? { high: status_high_speed, low: status_low_speed, orbital_enabled: true }
      : { speed: status_speed };

    this.framesService
      .FrameGatewayMasivePost(petition, query, obj)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp) => {
          this.handleResponseErrors(resp);
          if (!resp || !resp.errors) {
            onSuccess && onSuccess();
          }
        },
        () => {
          this.markAllAsError();
        }
      );
  }

  /**
   * Enciende el modo actualmente seleccionado para el subconjunto indicado.
   * @param feeders - subconjunto de alimentadores destino
   */
  private enableModeForGroup(feeders: any[]): void {
    const { view_mode_speed, status_high_speed, status_low_speed } = this.dataMasive;
    const query = `?id_in=${feeders.map((f) => f.id)}`;
    const enableFrame = view_mode_speed === "orbital" ? "enable_orbital" : "enable_speed";
    const enableBody =
      view_mode_speed === "orbital"
        ? { high: status_high_speed, low: status_low_speed, orbital_enabled: true }
        : {};

    this.framesService
      .Frame(enableFrame, query, enableBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp) => {
          this.handleResponseErrors(resp);
        },
        () => {
          this.markAllAsError();
        }
      );
  }

  /**
   * Apaga la velocidad (normal u orbital) en los alimentadores seleccionados.
   */
  turnOff(): void {
    this.startLoading("turnOff", "SPEED_CONFIG");
    const frame =
      this.dataMasive.view_mode_speed === "orbital" ? "disable_orbital" : "disable_speed";
    this.framesService
      .Frame(frame, this.buildQuery())
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp) => {
          this.handleResponseErrors(resp);
        },
        () => {
          this.markAllAsError();
        }
      );
  }

  /**
   * Enciende la velocidad (normal u orbital) en los alimentadores seleccionados.
   */
  turnOn(): void {
    this.startLoading("turnOn", "SPEED_CONFIG");
    const { view_mode_speed, status_high_speed, status_low_speed } = this.dataMasive;
    if (view_mode_speed === "orbital") {
      const obj = { high: status_high_speed, low: status_low_speed, orbital_enabled: true };
      this.framesService
        .Frame("enable_orbital", this.buildQuery(), obj)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (resp) => {
            this.handleResponseErrors(resp);
          },
          () => {
            this.markAllAsError();
          }
        );
    } else {
      this.framesService
        .Frame("enable_speed", this.buildQuery())
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (resp) => {
            this.handleResponseErrors(resp);
          },
          () => {
            this.markAllAsError();
          }
        );
    }
  }

  /**
   * Solicita el status actual de velocidad de los alimentadores seleccionados.
   */
  requestStatus(): void {
    this.startLoading("requestStatus", "SPEED_RESPONSE", "ORBITAL_SPEED");
    this.framesService
      .Frame("request_speed", this.buildQuery(), {})
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp) => {
          this.handleResponseErrors(resp);
        },
        () => {
          this.markAllAsError();
        }
      );
  }

  /**
   * Retorna los estados de alimentadores filtrados por nombre y/o estado de respuesta.
   * @returns FeederLoadStatus[]
   */
  filteredStatuses(): FeederLoadStatus[] {
    return this.feederStatuses.filter((s) => {
      const matchName =
        !this.filterName || s.name.toLowerCase().includes(this.filterName.toLowerCase());
      const matchStatus = !this.filterStatus || s.status === this.filterStatus;
      return matchName && matchStatus;
    });
  }

  /**
   * Cambia el valor de un input de velocidad en dataMasive.
   * @param value - valor recibido del input
   * @param field - propiedad a modificar
   */
  changeInput(value: any, field: keyof SpeedData): void {
    (this.dataMasive as any)[field] = value;
  }

  /**
   * Calcula el porcentaje relativo de un valor del slider para pintar el track hasta la posición actual.
   * @param value - valor actual del slider (entre 37.5 y 100)
   * @returns string con el porcentaje listo para usar como CSS custom property, ej. "60%"
   */
  getSliderPct(value: number): string {
    const min = 37.5;
    const max = 100;
    const pct = ((Number(value) - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, pct)).toFixed(1) + "%";
  }

  /**
   * Inicia el estado de carga para una acción, registrando los MACs pendientes y los tipos de frame esperados.
   * @param action - identificador de la acción iniciada
   * @param expectedTypes - tipos de frame que se consideran confirmación de respuesta
   */
  private startLoading(
    action: "send" | "turnOff" | "turnOn" | "requestStatus",
    ...expectedTypes: string[]
  ): void {
    this.loadingAction = action;
    this.pendingMacs = new Set<string>(this.selectedFeeders.map((f) => f.mac).filter(Boolean));
    this.expectedResponseTypes = new Set<string>(expectedTypes);
    this.feederStatuses = this.selectedFeeders.map((f) => ({
      name: f.name || f.mac || "—",
      mac: f.mac,
      status: "loading" as const
    }));
  }

  /**
   * Verifica si el frame recibido completa la espera de la acción en curso.
   * Cuando todos los MACs pendientes han respondido, limpia el estado de carga.
   * @param mac - MAC del alimentador que respondió
   * @param type - tipo de frame recibido
   */
  private checkLoadingComplete(mac: string, type: string, data?: any): void {
    if (this.expectedResponseTypes.has(type)) {
      const feeder = this.feederStatuses.find((s) => s.mac === mac);
      if (feeder) {
        feeder.status = "done";
        feeder.type = type;
        feeder.time = new Date().toLocaleTimeString();
        feeder.data = data || null;
      }
      this.pendingMacs.delete(mac);
      if (this.pendingMacs.size === 0) {
        this.loadingAction = null;
        this.expectedResponseTypes.clear();
      }
    }
  }

  /**
   * Marca como error el alimentador específico identificado por MAC cuando llega un ALERT_COMM.
   * @param mac - MAC del alimentador que falló
   * @param reason - mensaje de error recibido
   */
  private markFeederAsError(mac: string, reason: string): void {
    const feeder = this.feederStatuses.find((s) => s.mac === mac);
    if (feeder && feeder.status === "loading") {
      feeder.status = "error";
      feeder.failReason = reason;
      feeder.time = new Date().toLocaleTimeString();
    }
    this.pendingMacs.delete(mac);
    if (this.pendingMacs.size === 0) {
      this.loadingAction = null;
      this.expectedResponseTypes.clear();
    }
  }

  /**
   * Marca todos los alimentadores pendientes como error cuando falla la petición HTTP.
   */
  private markAllAsError(): void {
    this.feederStatuses.forEach((s) => {
      if (s.status === "loading") {
        s.status = "error";
        s.failReason = "Error al enviar el comando";
        s.time = new Date().toLocaleTimeString();
      }
    });
    this.loadingAction = null;
    this.pendingMacs.clear();
    this.expectedResponseTypes.clear();
  }

  /** Construye el query string con los ids de los alimentadores seleccionados */
  private buildQuery(): string {
    return `?id_in=${this.selectedFeeders.map((f) => f.id)}`;
  }

  /**
   * Procesa la respuesta HTTP y marca como error los alimentadores cuya MAC aparece en resp.errors.
   * @param resp - respuesta del servidor que puede contener un mapa de errores por MAC
   */
  private handleResponseErrors(resp: any): void {
    if (!resp || !resp.errors) {
      return;
    }
    Object.keys(resp.errors).forEach((mac) => {
      const messages: string[] = resp.errors[mac];
      const reason = messages && messages.length > 0 ? messages[0] : "Error de gateway";
      this.markFeederAsError(mac, reason);
    });
  }
}
